// Reveals streamed text at an even pace.
//
// Text arrives from the model in bursts — a word, then nothing, then half a sentence — and
// painting each burst as it lands makes the answer jerk forward. This keeps a cursor into the
// text and moves it every animation frame at a speed that changes gradually: it eases up when it
// falls behind and eases off as it catches up, so a burst reads as typing that picks up pace
// rather than a jump. The cursor moves by fractions of a character, so the pace is even at any
// frame rate, and the text that is left when the turn ends is typed out the same way.
//
// For anyone who prefers reduced motion, the full text shows at once.

import { useEffect, useRef, useState } from "react";

/** Characters per second when caught up — a calm reading pace. */
const BASE_RATE = 60;
/** How hard a backlog pulls the pace up: extra characters per second per character behind. */
const CATCH_UP = 2.5;
/** The fastest it ever goes, so a long burst still reads as typing. */
const MAX_RATE = 900;
/** How quickly the pace follows where it should be, per second; lower is gentler. */
const EASE = 6;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function useSmoothText(target: string, streaming: boolean): string {
  const [shown, setShown] = useState(() => (streaming ? "" : target));
  const position = useRef(streaming ? 0 : target.length);
  const rate = useRef(BASE_RATE);
  const latest = useRef(target);
  latest.current = target;
  const streamingRef = useRef(streaming);
  streamingRef.current = streaming;

  // Started by a turn, and running on after it until the text is all shown.
  const [typing, setTyping] = useState(streaming);
  if (streaming && !typing) setTyping(true);

  useEffect(() => {
    if (!typing) return;
    if (prefersReducedMotion()) {
      position.current = latest.current.length;
      setShown(latest.current);
      setTyping(false);
      return;
    }

    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const elapsed = Math.min((now - last) / 1000, 0.1);
      last = now;
      const text = latest.current;
      const behind = text.length - position.current;

      const wanted = Math.min(MAX_RATE, BASE_RATE + Math.max(0, behind) * CATCH_UP);
      rate.current += (wanted - rate.current) * Math.min(1, EASE * elapsed);
      position.current = Math.min(text.length, position.current + rate.current * elapsed);

      const visible = Math.floor(position.current);
      setShown((current) => (current.length === visible ? current : text.slice(0, visible)));

      if (visible >= text.length && !streamingRef.current) {
        setTyping(false);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [typing]);

  return typing ? shown : target;
}
